import { Link } from "react-router-dom";
import Seo from "@/components/Seo";
import PageHero from "@/components/PageHero";
import Reveal from "@/components/Reveal";
import { btnPrimary, btnOutline, overline } from "@/components/kit";
import { track } from "@/lib/track";
import { ArrowRight, ArrowUpRight, Users, Baby, Puzzle, CupSoda } from "lucide-react";
import { Helmet } from "react-helmet-async";
import { CONTACT } from "@/data/site";

const FAQ = [
  { q: "Peut-on réserver une animation seule, sans organisation complète ?", a: "Oui. Vous avez déjà le lieu et le traiteur ? Nous intervenons uniquement sur les animations et la coordination de soirée, dès 2 900 € HT." },
  { q: "Les animations conviennent-elles à un arbre de Noël CSE ?", a: "Oui : Père Noël et séance photo, spectacles, maquillage enfants, sculpture de ballons, ateliers créatifs et stands gourmands sont pensés pour les familles." },
  { q: "Les intervenants et le matériel sont-ils inclus ?", a: "Oui. Chaque animation inclut les intervenants, le matériel, l'installation et la coordination par votre chef de projet." },
  { q: "Comment composer le bon programme pour notre effectif ?", a: "Décrivez votre événement — date, lieu, effectif, public — et nous construisons un programme d'animations adapté à votre budget, avec une première réponse sous 24 h ouvrées." },
];

const SERVICE_JSONLD = {
  "@context": "https://schema.org",
  "@type": "Service",
  name: "Animations de Noël pour entreprises et CSE",
  description: "Animations de Noël pour entreprises et CSE : casino, blind test, mentaliste, Père Noël, magie, escape game, stands gourmands. Intervenants, matériel et coordination inclus.",
  provider: {
    "@type": "LocalBusiness",
    name: "Désir Events",
    url: "https://desir-events.com",
    telephone: CONTACT.phoneFixe,
    email: CONTACT.email,
    address: { "@type": "PostalAddress", streetAddress: "129 rue de Servient", addressLocality: "Lyon", postalCode: "69003", addressCountry: "FR" },
  },
  areaServed: { "@type": "Country", name: "France" },
};

const FAQ_JSONLD = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: FAQ.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
};

const BREADCRUMB_JSONLD = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: [
    { "@type": "ListItem", position: 1, name: "Accueil", item: "https://desir-events.com/" },
    { "@type": "ListItem", position: 2, name: "Noël en entreprise" },
    { "@type": "ListItem", position: 3, name: "Animations de Noël", item: "https://desir-events.com/animation-noel-entreprise" },
  ],
};

const CATEGORIES = [
  { icon: Users, title: "Pour les adultes", items: ["Casino fictif", "Quiz musical et blind test", "Karaoké", "Mentaliste ou magicien close-up", "Photobooth de Noël", "Animation cocktail (bar à cocktails)", "DJ et dancefloor"] },
  { icon: Baby, title: "Pour les familles", items: ["Père Noël et séance photo", "Spectacle de magie", "Cirque ou marionnettes", "Maquillage enfants", "Sculpture de ballons", "Ateliers créatifs de Noël"] },
  { icon: Puzzle, title: "Participatives", items: ["Challenge en équipes", "Escape game de Noël", "Concours de pulls moches", "Ateliers culinaires (bûche, chocolats)"] },
  { icon: CupSoda, title: "Gourmandes", items: ["Bar à chocolat chaud", "Stand crêpes", "Stand gaufres", "Marrons chauds", "Cocktails de Noël (avec ou sans alcool)"] },
];

export default function AnimationNoel() {
  return (
    <>
      <Seo
        title="Animation Noël entreprise | 20 animations pour salariés et CSE"
        description="Animations de Noël pour entreprises et CSE : casino, blind test, mentaliste, Père Noël, magie, escape game, stands gourmands. Animation seule dès 2 900 € HT, devis sous 24 h ouvrées."
        path="/animation-noel-entreprise"
      />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(SERVICE_JSONLD)}</script>
        <script type="application/ld+json">{JSON.stringify(FAQ_JSONLD)}</script>
        <script type="application/ld+json">{JSON.stringify(BREADCRUMB_JSONLD)}</script>
      </Helmet>
      <PageHero eyebrow="Animations de fin d'année" title={<>Animations de Noël<br /><span className="italic text-lime-dim">pour entreprises et CSE</span></>} />

      <section className="mx-auto max-w-[1440px] px-5 py-16 md:px-10" data-testid="animation-noel-intro">
        <Reveal>
          <p className="max-w-3xl text-lg leading-relaxed text-ink/70">
            Vous avez déjà le lieu et le traiteur ? Nous apportons l'ambiance. Plus de 20 animations de Noël éprouvées, classées par public — des salariés aux enfants des arbres de Noël CSE. Animation seule <strong>dès 2 900 € HT</strong>, intégrable à toutes nos formules de soirée.
          </p>
        </Reveal>
      </section>

      <section className="border-t border-ink/10 bg-mist" data-testid="animation-noel-categories">
        <div className="mx-auto max-w-[1440px] px-5 py-16 md:px-10 md:py-24">
          <div className="grid gap-5 md:grid-cols-2">
            {CATEGORIES.map((c, i) => (
              <Reveal key={c.title} delay={i % 2}>
                <div className="h-full rounded-2xl border border-ink/10 bg-white p-8" data-testid={`animation-noel-${c.title.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "")}`}>
                  <c.icon size={20} className="text-lime-dim" />
                  <h2 className="mt-4 font-serif text-2xl text-ink">{c.title}</h2>
                  <ul className="mt-5 grid gap-2 sm:grid-cols-2">
                    {c.items.map((it) => (
                      <li key={it} className="flex items-start gap-2 text-sm text-ink/70">
                        <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-lime-dim" />{it}
                      </li>
                    ))}
                  </ul>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={2}>
            <p className="mt-8 max-w-2xl text-sm text-ink/50">
              Toutes les animations incluent les intervenants, le matériel, l'installation et la coordination par votre chef de projet. Composez votre programme ou laissez-nous le construire selon votre effectif et votre budget.
            </p>
          </Reveal>
        </div>
      </section>

      {/* FAQ */}
      <section className="border-t border-ink/10 bg-white" data-testid="animation-noel-faq">
        <div className="mx-auto max-w-[1440px] px-5 py-16 md:px-10 md:py-24">
          <Reveal><span className={overline}>Questions fréquentes</span></Reveal>
          <div className="mt-10 grid gap-4 md:grid-cols-2">
            {FAQ.map((f, i) => (
              <Reveal key={f.q} delay={i % 2}>
                <div className="h-full rounded-2xl border border-ink/10 bg-mist p-7" data-testid={`animation-noel-faq-${i}`}>
                  <h3 className="font-serif text-xl leading-snug text-ink">{f.q}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-ink/60">{f.a}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Maillage */}
      <section className="border-t border-ink/10 bg-white" data-testid="animation-noel-related">
        <div className="mx-auto max-w-[1440px] px-5 py-14 md:px-10">
          <Reveal><span className={overline}>Complétez votre Noël</span></Reveal>
          <div className="mt-6 flex flex-wrap gap-3">
            {[
              { label: "Soirée de Noël d'entreprise à Lyon", to: "/fete-de-noel-entreprise/lyon" },
              { label: "Arbre de Noël CSE à Lyon", to: "/evenements-cse/lyon" },
              { label: "Événements CSE — vue d'ensemble", to: "/evenements-cse" },
              { label: "Décoration de Noël pour magasins", to: "/decoration-noel-magasin" },
              { label: "Décoration de Noël en entreprise", to: "/decoration-noel-entreprise" },
            ].map((r) => (
              <Link key={r.to} to={r.to} className="group flex items-center gap-2 rounded-full border border-ink/15 bg-mist px-5 py-2.5 text-sm text-ink/70 transition-colors hover:border-lime-dim hover:text-ink" data-testid={`animation-noel-related-${r.to.replace(/[/#]/g, "")}`}>
                {r.label} <ArrowUpRight size={14} className="text-lime-dim" />
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-[1440px] px-5 py-20 md:px-10 md:py-28" data-testid="animation-noel-cta">
        <Reveal><h2 className="max-w-3xl font-serif text-4xl font-medium leading-tight tracking-tight text-ink md:text-5xl">Votre programme d'animations, <span className="italic text-lime-dim">sur mesure.</span></h2></Reveal>
        <Reveal delay={2}>
          <div className="mt-10 flex flex-wrap gap-4">
            <Link to="/devis" onClick={() => track("click_devis")} className={btnPrimary} data-testid="animation-noel-devis-btn">Recevoir une première proposition <ArrowRight size={16} /></Link>
            <a href={`tel:${CONTACT.phoneMobile.replace(/\s/g, "")}`} onClick={() => track("click_phone")} className={btnOutline}>Appeler le {CONTACT.phoneMobile}</a>
          </div>
        </Reveal>
      </section>
    </>
  );
}
